import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnDestroy, OnInit} from '@angular/core';
import {CreateCampaignInitPayload} from '../campaigns/campaigns.component';
import {GetAllCampaignPayloadModel} from '../campaigns/campaign_interfaces';
import {Subject, takeUntil} from 'rxjs';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {CurrentCrudSelectBehaviorServiceAsString, SnackBarService} from '../objectPassingService';
import {Router} from '@angular/router';
import {CampaignService} from '../campaigns/campaigns_service';
import {environment} from '../../environments/environment';
import {CreateAdgroupInitPayload} from '../adgroups/adgroups.component';
import {MatButton} from '@angular/material/button';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatProgressBar} from '@angular/material/progress-bar';

import {MatSlideToggle} from '@angular/material/slide-toggle';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatList, MatListItem} from '@angular/material/list';
import {MatDivider} from '@angular/material/divider';
import {MatDatepickerModule} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import {MatNativeDateModule, MatOption, provideNativeDateAdapter} from '@angular/material/core';
import {MatCardModule} from '@angular/material/card';
import {MatSelect} from '@angular/material/select';


function isCreateCampaignInitPayload(obj: any): obj is CreateCampaignInitPayload {
  return (
    obj &&
    typeof obj === 'object' &&
    obj.is_edit === false &&
    Object.keys(obj).length === 1
  );
}

function isGetAllCampaignPayloadModel(obj: any): obj is GetAllCampaignPayloadModel {
  /*
  The reason we kept both isCreateAdInitPayload() and isGetAllAdPayloadModel() was:
	•	To future-proof (in case is_edit accidentally shows up in GetAllAdPayloadModel)
	•	To keep the checks isolated and testable
	•	To avoid assumptions in the “else” branch
   */
  return obj && typeof obj === 'object' && typeof obj.name === 'string';
}

@Component({
  selector: 'app-campaigns-meta',
  imports: [
    MatButton,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatProgressBar,
    ReactiveFormsModule,
    MatSlideToggle,
    MatCheckbox,
    FormsModule,
    MatList,
    MatListItem,
    MatDivider,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatCardModule,
    MatOption,
    MatSelect
],
  standalone: true,
  providers: [provideNativeDateAdapter()],
  templateUrl: './campaigns-meta.component.html',
  styleUrl: './campaigns-meta.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignsMetaComponent implements OnInit , OnDestroy {

  private destroyRef = inject(DestroyRef);
  destroyed = new Subject<void>();

  is_edit = false;
  is_form_error: boolean = false;
  form_error_message: string = "";
  myForm!: FormGroup;
  title_text = "Create Adgroup";
  submit_text = "Create"


  client_id: number = 0
  client_slug: string = '';
  campaign_group_sequence_id: string = '';
  campaign_group_id: string = '';
  campaign_id: string = '';
  is_loading = true;
  hoursOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];


  constructor(
    private fb: FormBuilder,
    private snackBarService: SnackBarService,
    public router: Router, //all components need this
    private currentCrudSelectBehaviorService: CurrentCrudSelectBehaviorServiceAsString,
    private campaignService: CampaignService,
  ) {

  } // End of constructor

  ngOnInit(): void {
    this.client_id = parseInt(localStorage.getItem('client_id') || '0');
    this.client_slug = localStorage.getItem('client_slug') || '';
    this.campaign_group_sequence_id = localStorage.getItem('campaign_group_sequence_id') || '';
    this.campaign_group_id = localStorage.getItem('campaign_group_id') || '';
    this.campaign_id = localStorage.getItem('campaign_id') || '';


    // Use localStorage data on reload
    const savedCampaignMeta = localStorage.getItem('campaignsaved');

    if (savedCampaignMeta) {
      // now if I want I can ... if exists go back to ads rather than having
      // to deal with updating saved storage e.g. on input update saved storage
      // will keep on for development for now
      //this.backToAds()

      if (environment.environment == 'production') {
        this.backToCampaigns()
      }

      try {
        const parsed = JSON.parse(savedCampaignMeta);
        if (isCreateCampaignInitPayload(parsed)) {
          const obj: CreateAdgroupInitPayload = parsed;
          console.log('[Reload] Editing state:', obj);
          this.setForm(); // Or pass in data if needed
          this.is_edit = obj.is_edit;
        } else if (isGetAllCampaignPayloadModel(parsed)) {
          const obj: GetAllCampaignPayloadModel = parsed;
          console.log('[Reload] Ad Data:', obj);
          this.setForm(obj);
          this.is_edit = true;
          this.campaign_id = obj.campaign_id;
        }
      } catch (e) {
        console.error('Error parsing localStorage adsaved:', e);
      }
    } // end if savedAdMeta

    // Subscriptions from services (live interaction)
    this.currentCrudSelectBehaviorService.current$.pipe(takeUntil(this.destroyed)).subscribe(x => {
      if (!x) return;

      try {
        const parsed = JSON.parse(x);

        if (isCreateCampaignInitPayload(parsed)) {
          console.log('[Stream] Create Payload:', parsed);
          this.setForm();
          this.is_edit = parsed.is_edit;
        } else if (isGetAllCampaignPayloadModel(parsed)) {
          console.log('[Stream] Edit Payload:', parsed);
          this.setForm(parsed);
          this.is_edit = true;
        } else {
          throw new Error('Unknown payload structure');
        }

        localStorage.setItem('campaignsaved', JSON.stringify(parsed));
      } catch (e) {
        console.error('Error parsing stream payload:', e);
      }
    }); // end subscription
  } // End of ngOnInit

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  } // end ngOnDestroy

  setForm(data?: GetAllCampaignPayloadModel) {

    if (!data) {

      const today = new Date().toISOString().split('T')[0];

      this.myForm = new FormGroup({
        name: new FormControl('', [Validators.required]),
        status: new FormControl(false),
        frequency_cap_daily: new FormControl(0),
        frequency_cap_weekly: new FormControl(0),
        frequency_cap_total: new FormControl(0),
        recency_cap_in_hours:  new FormControl(0),
        start_date: new FormControl(today),
        end_date: new FormControl(today),
        audience: this.fb.group({
          hod: [],
          dow: [],
        })
      });

    } else {
      this.submit_text = "Edit"


      let is_status =false;
      if (data.status == "active") {
        is_status = true;
      } else {
        is_status = false;
      }


      let hod_str = '';
      hod_str = data.audience.hod && data.audience.hod.length > 0 ? data.audience.hod.join(',') : '';
      console.log('hod_str',hod_str)

      let dow_str = '';
      if (data.audience.dow && data.audience.dow.length > 0) {
        dow_str = data.audience.dow.join(',');
      } else {
        dow_str = '';
      }
      console.log('dow_str',dow_str)

      this.myForm = this.fb.group({
        name: [data.name],
        status: [is_status],
        frequency_cap_daily: [data.frequency_cap_daily],
        frequency_cap_weekly: [data.frequency_cap_weekly],
        frequency_cap_total: [data.frequency_cap_total],
        recency_cap_in_hours: [data.recency_cap_in_hours],
        start_date: [data.start_date],
        end_date: [data.end_date],
        audience: this.fb.group({
          hod: [hod_str],
          dow: [dow_str],
        })
      });
    }

    this.is_loading = false
  } // setForm

  backToCampaigns() {
    localStorage.removeItem('campaignsaved')
    //this.router.navigate(['/nav-campaigns', {outlets: {admeta: ['ads']}}]);
    this.router.navigate(['/nav-campaigns',  { outlets: { admeta: ['campaigns'] } }]).then(r => console.log('redirect to campaigns'));
  } // end backToCampaigns

  onFocus(){
    console.log('onFocus')
    this.is_form_error = false;
    this.form_error_message = "";
  } // end onFocus

  // toYYYYMMDD(dateInput: string | Date): string {
  //   // If already in YYYY-MM-DD format, return as is
  //   if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
  //     return dateInput;
  //   }
  //
  //   const d = new Date(dateInput);
  //   if (isNaN(d.getTime())) {
  //     throw new Error(`Invalid date: ${dateInput}`);
  //   }
  //
  //   return d.toISOString().slice(0, 10);
  // }

  toYYYYMMDD(dateInput: string | Date): string {
    const d = new Date(dateInput);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }



  onSubmit(){

    this.is_loading = true;
    let form_data = this.myForm.value;
    form_data['client_id'] =  this.client_id  //Math.floor(parseInt(form_data['client_id'], 10));
    console.log(form_data)

    let status = 'paused';
    if (form_data['status'] == true) {
      status = 'active';
    } else {
      status = 'paused';
    }
    form_data['status'] = status;

    let hod: number[] = [];
    if (form_data['audience']['hod']) {
      hod = form_data['audience']['hod'].split(',').map(Number);
    }

    let dow: number[] = [];
    if (form_data['audience']['dow']) {
      dow = form_data['audience']['dow'].split(',').map(Number);
    }

    form_data['audience']['hod'] = hod;
    form_data['audience']['dow'] = dow;
    form_data.start_date = this.toYYYYMMDD(form_data.start_date);
    form_data.end_date = this.toYYYYMMDD(form_data.end_date);


    console.log('form_datawow!!!!!!!!!!!',form_data)


    if (!this.is_edit) {

      this.campaignService.post(`adserver/campaign/create/${this.client_id}/${this.campaign_group_sequence_id}/${this.campaign_group_id}`, form_data).pipe(takeUntil(this.destroyed)).subscribe(x => {
        this.snackBarService.announceSackBar('Campaign Created');
        this.is_loading = false;
        this.backToCampaigns();
      }, error => {
        console.log('error',error)
      });

    } else {
      //ITS BECAUSE OF THE DATES ON EDIT AND RELOAD.  NEED TO FIX THIS
      console.log('we are editing')
      let endpoint = `adserver/campaign/${this.campaign_id}`;
      console.log('endpoint',endpoint)
      this.campaignService.put(endpoint, form_data).subscribe(x => {
        this.is_loading = false;
        this.snackBarService.announceSackBar('Campaign Updated');
        this.backToCampaigns();
      }, error => {
        this.is_loading = false;
        this.snackBarService.announceSackBar('Campaign Update Error');
        console.log('error',error)
      });


    } //end if

  } // end OnSubmit










} // End of CampaignsMetaComponent
